import Link from 'next/link'
import { formatDate } from '@/lib/utils'
import { getNewsHref } from '@/lib/news-url'
import NewsCategoryTag from '../NewsCategoryTag'
import { VideoPlayOverlay, GalleryCountBadge } from '../media/MediaThumbnailOverlay'
import ImagePlaceholder from './ImagePlaceholder'
import RetryableImage from './RetryableImage'
import LiveBadge, { isLiveNews } from '../LiveBadge'
import { getNewsDisplayTitle } from '@/lib/news-display'
import type { News } from '@/lib/types'

export type NewsWithImageUrl = News & { imageUrl?: string | null }

export type NewsCardVariant = 'grid' | 'featured' | 'hero'

interface NewsCardProps {
  item: NewsWithImageUrl
  variant?: NewsCardVariant
  showDate?: boolean
  priority?: boolean
  plain?: boolean
}

export default function NewsCard({
  item,
  variant = 'grid',
  showDate = true,
  priority = false,
  plain = false,
}: NewsCardProps) {
  const href = getNewsHref(item)
  const imageUrl = item.imageUrl?.trim() || ''
  const displayTitle = getNewsDisplayTitle(item)
  const photoCount = item._count?.photos || item.photos?.length || 0
  const isVideo = Boolean(item.video?.trim()) || item.isType === 2
  const isGallery = item.isType === 1
  const live = isLiveNews(item)

  if (variant === 'hero') {
    return (
      <article className="news-card-surface group mb-3 overflow-hidden rounded-[5px] border border-gray-100 bg-white dark:border-slate-800 dark:bg-slate-900 sm:mb-4 md:mb-5">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-gray-100 dark:bg-slate-800 sm:aspect-[2/1] md:aspect-[21/9]">
          <Link href={href} className="block h-full">
            {imageUrl ? (
              <RetryableImage
                src={imageUrl}
                alt={displayTitle}
                fill
                priority={priority}
                className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                sizes="(max-width: 768px) 100vw, 1280px"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800" />
            )}
          </Link>
          {live ? <LiveBadge variant="overlay" /> : null}
          <div className="featured-news-overlay pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-12 sm:p-4 sm:pt-16">
            <div className="pointer-events-auto flex flex-wrap items-center gap-2">
              <NewsCategoryTag tags={item.tags} category={item.category} surface="dark" />
              {live ? <LiveBadge variant="pill" /> : null}
            </div>
            <Link href={href} className="pointer-events-auto">
              <h2 className="featured-news-title mt-1.5 line-clamp-3 text-base font-bold leading-snug sm:line-clamp-2 sm:text-lg md:text-xl">
                {displayTitle}
              </h2>
            </Link>
            <p className="featured-news-meta mt-1.5 flex items-center gap-1 text-[11px] sm:text-xs">
              <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatDate(item.createdAt)}
            </p>
          </div>
        </div>
      </article>
    )
  }

  if (variant === 'featured') {
    return (
      <article className="news-card-surface group overflow-hidden rounded-[5px] border border-gray-100 bg-white dark:border-slate-800 dark:bg-slate-800">
        <div className="relative aspect-[16/10] overflow-hidden bg-gray-100 dark:bg-slate-700 sm:aspect-auto sm:h-48">
          <Link href={href} className="relative block h-full w-full">
            {imageUrl ? (
              <RetryableImage
                src={imageUrl}
                alt={displayTitle}
                fill
                className="object-cover transform-gpu motion-safe:transition-transform motion-safe:duration-300 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                <ImagePlaceholder className="h-12 w-12" />
              </div>
            )}
          </Link>
          {live ? <LiveBadge variant="overlay" /> : null}
          {isGallery && (
            <GalleryCountBadge count={photoCount} className="right-2 top-2 z-10" />
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-3 pt-8">
            <div className="pointer-events-auto">
              <NewsCategoryTag tags={item.tags} category={item.category} surface="dark" />
            </div>
          </div>
        </div>
        <Link href={href} className="block bg-white p-3 sm:p-4 dark:bg-slate-800">
          {live ? (
            <div className="mb-1.5">
              <LiveBadge variant="pill" />
            </div>
          ) : null}
          <h3 className="line-clamp-2 text-sm font-bold text-gray-900 transition-colors group-hover:text-red-600 sm:text-base dark:text-gray-100 dark:group-hover:text-red-400">
            {displayTitle}
          </h3>
        </Link>
      </article>
    )
  }

  return (
    <article
      className={`news-card-surface group min-w-0 overflow-hidden rounded-[5px] ${
        plain
          ? 'border-0 bg-transparent dark:bg-transparent'
          : 'border border-gray-100 bg-white dark:border-slate-800 dark:bg-slate-800'
      }`}
    >
      <div className="block min-w-0 p-0">
        <div className="relative aspect-video w-full overflow-hidden rounded-none bg-gray-100 dark:bg-slate-700">
          <Link href={href} className="relative block h-full w-full" aria-label={displayTitle}>
            {imageUrl ? (
              <RetryableImage
                src={imageUrl}
                alt={displayTitle}
                fill
                priority={priority}
                className="object-cover transform-gpu motion-safe:transition-transform motion-safe:duration-300 group-hover:scale-105"
                sizes="(max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                <ImagePlaceholder />
              </div>
            )}
          </Link>
          {live ? (
            <LiveBadge
              variant="overlay"
              className="!top-1.5 !left-1.5 scale-90 sm:scale-100 sm:!top-2 sm:!left-2"
            />
          ) : null}
          {isVideo && <VideoPlayOverlay size="grid" />}
          {isGallery && <GalleryCountBadge count={photoCount} />}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2.5 sm:p-3">
            <NewsCategoryTag tags={item.tags} category={item.category} surface="dark" />
          </div>
        </div>

        <div className="min-w-0">
          <Link
            href={href}
            className="flex min-w-0 flex-col justify-center p-2.5 sm:p-3 lg:p-3.5"
          >
            {live ? (
              <div className="mb-1.5">
                <LiveBadge variant="pill" />
              </div>
            ) : null}
            <h3 className="mb-1 line-clamp-2 text-[13px] font-bold leading-snug text-gray-900 transition-colors group-hover:text-red-600 dark:text-gray-100 dark:group-hover:text-red-400 sm:mb-1.5 sm:text-[0.9375rem] lg:text-base">
              {displayTitle}
            </h3>
            {showDate && (
              <p className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 sm:text-xs">
                <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="truncate">{formatDate(item.createdAt)}</span>
              </p>
            )}
          </Link>
        </div>
      </div>
    </article>
  )
}
